import React, { useState } from 'react';
import { Menu, X, ChevronRight, CheckCircle2, Shield, Users, BarChart3, FileCheck, Clock, Zap, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { mockFormSubmission, mockGAEvent } from '../mock/mockData';
import { CookieConsent } from './CookieConsent';

export const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    role: '',
    email: '',
    volume: '',
    message: '',
    honeypot: ''  // Spam prevention field
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
    mockGAEvent('navigation_click', { section: sectionId });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    mockGAEvent('form_submission', { form_name: 'contact_form' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Submission failed');
      }

      setFormStatus({
        type: 'success',
        message: data.message
      });
      
      // Clear form only on success
      setFormData({
        fullName: '',
        companyName: '',
        role: '',
        email: '',
        volume: '',
        message: '',
        honeypot: ''
      });
      
      mockGAEvent('lead_generated', { company: formData.companyName });
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: error.message || 'An error occurred. Please try again or contact us directly at gkioka95@gmail.com'
      });
      // Do NOT clear form on error - user keeps their typed message
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-navy-dark tracking-tight">
                Weight Prescriber Network
              </h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('about')}
                className="text-sm font-medium text-gray-700 hover:text-teal-700 transition-colors"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('model')}
                className="text-sm font-medium text-gray-700 hover:text-teal-700 transition-colors"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection('governance')}
                className="text-sm font-medium text-gray-700 hover:text-teal-700 transition-colors"
              >
                Clinical Governance
              </button>
              <button
                onClick={() => scrollToSection('standards')}
                className="text-sm font-medium text-gray-700 hover:text-teal-700 transition-colors"
              >
                Prescriber Standards
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-sm font-medium text-gray-700 hover:text-teal-700 transition-colors"
              >
                Contact
              </button>
              <a
                href="https://www.linkedin.com/company/weight-prescriber-network/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-teal-700 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-teal-700 hover:bg-teal-800 text-white"
              >
                Request a Confidential Discussion
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-teal-700"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
                >
                  About
                </button>
                <button
                  onClick={() => scrollToSection('model')}
                  className="text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
                >
                  How It Works
                </button>
                <button
                  onClick={() => scrollToSection('governance')}
                  className="text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
                >
                  Clinical Governance
                </button>
                <button
                  onClick={() => scrollToSection('standards')}
                  className="text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
                >
                  Prescriber Standards
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
                >
                  Contact
                </button>
                <a
                  href="https://www.linkedin.com/company/weight-prescriber-network/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
                >
                  <Linkedin size={18} />
                  LinkedIn
                </a>
                <Button
                  onClick={() => scrollToSection('contact')}
                  className="bg-teal-700 hover:bg-teal-800 text-white mx-4"
                >
                  Request Discussion
                </Button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-navy-dark via-charcoal to-navy-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                Weight management pharmacist prescribers, integrated into your workflow
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed">
                Scalable, per-task prescribing capacity aligned to weight management prescribing models and integrated into your governance framework.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => scrollToSection('contact')}
                  size="lg"
                  className="bg-teal-700 hover:bg-teal-800 text-white text-lg px-8"
                >
                  Request a Confidential Discussion
                  <ChevronRight className="ml-2" size={20} />
                </Button>
                <Button
                  onClick={() => scrollToSection('model')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-navy-dark text-lg px-8"
                >
                  Explore How It Works
                </Button>
              </div>
              <p className="text-sm text-gray-400 pt-4">
                B2B only. Prescribers work within your SOPs, systems and clinical pathway.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_prescriber-infra/artifacts/n7ey7qx7_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2011_37_39%20PM.png"
                alt="Remote prescribing consultation"
                className="rounded-lg shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-dark mb-6">
              About Weight Prescriber Network
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
              We provide a pharmacist-led prescribing capacity layer for regulated UK weight management providers. 
              Our prescribers operate within your SOPs, systems and clinical pathway, supporting scale without 
              compromising governance. We focus on protocol-driven workflows, structured onboarding and operational consistency.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto mt-4">
              UK-based team with governance-first delivery and audit-ready documentation. 
              Experienced in supporting regulated services and inspection expectations (GPhC/CQC-aligned environments).
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="text-teal-700" size={32} />
              </div>
              <h3 className="font-bold text-navy-dark mb-2">Per-task capacity</h3>
              <p className="text-gray-600 text-sm">Scales with demand</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-teal-700" size={32} />
              </div>
              <h3 className="font-bold text-navy-dark mb-2">Governance-aligned</h3>
              <p className="text-gray-600 text-sm">You retain clinical oversight</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-teal-700" size={32} />
              </div>
              <h3 className="font-bold text-navy-dark mb-2">Fast onboarding</h3>
              <p className="text-gray-600 text-sm">Structured integration into your workflow</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Capacity Problem Section */}
      <section className="py-20 bg-light-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_prescriber-infra/artifacts/b3mcf2fx_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2011_38_16%20PM.png"
                alt="Capacity management and prescriber allocation"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-navy-dark">
                When demand outgrows prescribing capacity
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Online weight management services are experiencing sustained growth in prescribing demand. 
                Recruitment delays, rising clinical costs and inconsistent prescriber availability create 
                operational strain and capacity risk.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Scaling safely requires structured prescribing capacity — not ad hoc staffing.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Weight Prescriber Network provides pharmacy-led infrastructure designed for high-volume, 
                protocol-driven digital prescribing environments.
              </p>

              <div className="grid grid-cols-1 gap-4 pt-6">
                <div className="flex items-start gap-3">
                  <Clock className="text-teal-700 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-semibold text-navy-dark">Longer review times and delayed turnarounds</h4>
                    <p className="text-gray-600">Extended wait times impact patient satisfaction and retention</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BarChart3 className="text-teal-700 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-semibold text-navy-dark">Peaks in demand causing backlogs</h4>
                    <p className="text-gray-600">Volume surges overwhelm fixed capacity</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="text-teal-700 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-semibold text-navy-dark">Governance risk from inconsistent processes</h4>
                    <p className="text-gray-600">Variable quality threatens regulatory compliance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-dark mb-6">
              Structured, Pharmacy-Led Prescribing Capacity
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We supply experienced pharmacist independent prescribers on a per-task basis to increase 
              prescribing throughput while maintaining disciplined protocol alignment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="text-teal-700" size={24} />
                </div>
                <h3 className="text-lg font-bold text-navy-dark mb-2">
                  Per-task capacity that scales with demand
                </h3>
                <p className="text-gray-600">
                  Flexible resource allocation matched to your workflow requirements
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle2 className="text-teal-700" size={24} />
                </div>
                <h3 className="text-lg font-bold text-navy-dark mb-2">
                  No fixed employment overheads or long-term commitments
                </h3>
                <p className="text-gray-600">
                  Reduce operational costs while maintaining service levels
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <FileCheck className="text-teal-700" size={24} />
                </div>
                <h3 className="text-lg font-bold text-navy-dark mb-2">
                  Operational alignment to your SOPs and clinical pathway
                </h3>
                <p className="text-gray-600">
                  Seamless integration with existing governance frameworks
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="text-teal-700" size={24} />
                </div>
                <h3 className="text-lg font-bold text-navy-dark mb-2">
                  Designed for protocol-driven weight management prescribing models
                </h3>
                <p className="text-gray-600">
                  Structured prescribing processes for weight management services
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="text-teal-700" size={24} />
                </div>
                <h3 className="text-lg font-bold text-navy-dark mb-2">
                  Operational oversight and performance monitoring
                </h3>
                <p className="text-gray-600">
                  Continuous quality assurance and coordination built in
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="text-teal-700" size={24} />
                </div>
                <h3 className="text-lg font-bold text-navy-dark mb-2">
                  Structured onboarding and training
                </h3>
                <p className="text-gray-600">
                  Role-based training aligned to your SOPs, products and escalation pathways
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="text-teal-700" size={24} />
                </div>
                <h3 className="text-lg font-bold text-navy-dark mb-2">
                  Operational oversight and performance monitoring
                </h3>
                <p className="text-gray-600">
                  Performance monitoring, QA sampling and audit-ready reporting to support governance
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-teal-700 hover:bg-teal-800 text-white"
            >
              Request a Confidential Discussion
            </Button>
          </div>
        </div>
      </section>

      {/* Operational Model Section */}
      <section id="model" className="py-20 bg-light-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-dark mb-6">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                step: '01',
                title: 'You define SOPs, governance and prescribing pathway',
                description: 'Establish your clinical protocols and operational requirements'
              },
              {
                step: '02',
                title: 'We align prescribers to your workflow, systems and clinical pathway',
                description: 'Structured onboarding ensures seamless integration'
              },
              {
                step: '03',
                title: 'You receive scalable prescribing capacity',
                description: 'Flexible capacity that adjusts to demand while maintaining governance'
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow h-full border-t-4 border-teal-700">
                  <div className="text-5xl font-bold text-teal-700 opacity-20 mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-navy-dark mb-3 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {item.description}
                  </p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="text-teal-700" size={32} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-teal-700">
            <p className="text-gray-700 text-center font-medium">
              We do not replace your governance — we provide operational capacity within your framework.
            </p>
          </div>
        </div>
      </section>

      {/* Prescriber Standards Section */}
      <section id="standards" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-dark mb-6">
              Prescriber Standards
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Our network is built for reliable, protocol-driven prescribing in regulated environments.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Card 1: Eligibility */}
            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle2 className="text-teal-700" size={28} />
                </div>
                <h3 className="text-xl font-bold text-navy-dark mb-3">
                  Eligibility
                </h3>
                <p className="text-gray-700 mb-4 text-sm">
                  UK-registered pharmacist prescribers, verified and ready to work within regulated pathways.
                </p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">UK pharmacist independent prescribers</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">Professional indemnity in place</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">Secure system access</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Clinical discipline */}
            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="text-teal-700" size={28} />
                </div>
                <h3 className="text-xl font-bold text-navy-dark mb-3">
                  Clinical discipline
                </h3>
                <p className="text-gray-700 mb-4 text-sm">
                  Protocol-driven prescribing with defined escalation routes and safety controls.
                </p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">Weight management prescribing models (protocol-driven)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">Escalation routes for clinical queries and red flags</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">Governance-first approach within client SOPs</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Operational controls */}
            <Card className="border-2 border-gray-200 hover:border-teal-700 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <FileCheck className="text-teal-700" size={28} />
                </div>
                <h3 className="text-xl font-bold text-navy-dark mb-3">
                  Operational controls
                </h3>
                <p className="text-gray-700 mb-4 text-sm">
                  Structured onboarding and ongoing quality review to maintain consistency at scale.
                </p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">Structured onboarding aligned to client SOPs and systems</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">Ongoing performance monitoring and feedback</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-gray-700">Audit-ready operational documentation (where applicable)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_prescriber-infra/artifacts/3x2b9z81_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2011_38_11%20PM.png"
              alt="Remote prescriber conducting clinical consultation"
              className="rounded-lg shadow-lg w-full max-w-4xl mx-auto h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Governance & Accountability Section */}
      <section id="governance" className="py-20 bg-light-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_prescriber-infra/artifacts/2fxld27b_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2011_38_20%20PM.png"
                alt="Secure prescribing workflow and governance systems"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-navy-dark">
                Governance-Aligned Infrastructure
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                We integrate into existing governance frameworks without assuming clinical control. 
                Prescribers operate within the engaging pharmacy's SOPs, systems and clinical pathway.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our processes are built to support audit readiness and inspection expectations.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We provide operational coordination and performance monitoring while the engaging 
                pharmacy retains clinical governance and oversight.
              </p>

              <div className="grid gap-4 pt-6">
                <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-700">
                  <h4 className="font-bold text-navy-dark mb-2">Clear accountability boundaries</h4>
                  <p className="text-gray-600">
                    Defined roles and responsibilities maintain regulatory compliance
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-700">
                  <h4 className="font-bold text-navy-dark mb-2">Audit-readiness support (operational)</h4>
                  <p className="text-gray-600">
                    Documentation and reporting aligned with inspection requirements
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-700">
                  <h4 className="font-bold text-navy-dark mb-2">Standardised pathways and safety controls</h4>
                  <p className="text-gray-600">
                    Consistent application of client SOPs and safeguarding procedures
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-dark mb-6">
              Confidential Discussion
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              If you are preparing to scale your weight management service or experiencing prescribing 
              capacity constraints, we invite a structured and confidential discussion.
            </p>
          </div>

          {formStatus.type === 'success' && (
            <div className="mb-8 p-6 bg-teal-50 border-2 border-teal-700 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-bold text-teal-900 mb-1">Enquiry Submitted Successfully</h3>
                <p className="text-teal-800">{formStatus.message}</p>
              </div>
            </div>
          )}

          {formStatus.type === 'error' && (
            <div className="mb-8 p-6 bg-red-50 border-2 border-red-500 rounded-lg">
              <p className="text-red-800">{formStatus.message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 bg-light-grey p-8 rounded-lg shadow-lg">
            {/* Honeypot field - hidden from users, catches bots */}
            <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleInputChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-navy-dark font-medium">
                  Full Name *
                </Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="border-gray-300 focus:border-teal-700 focus:ring-teal-700"
                  placeholder="John Smith"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-navy-dark font-medium">
                  Company Name *
                </Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="border-gray-300 focus:border-teal-700 focus:ring-teal-700"
                  placeholder="Your Company Ltd"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="role" className="text-navy-dark font-medium">
                  Role / Title *
                </Label>
                <Input
                  id="role"
                  name="role"
                  type="text"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  className="border-gray-300 focus:border-teal-700 focus:ring-teal-700"
                  placeholder="e.g., Superintendent Pharmacist"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-navy-dark font-medium">
                  Work Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="border-gray-300 focus:border-teal-700 focus:ring-teal-700"
                  placeholder="your.email@company.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="volume" className="text-navy-dark font-medium">
                Estimated Monthly Prescribing Volume (optional)
              </Label>
              <Input
                id="volume"
                name="volume"
                type="text"
                value={formData.volume}
                onChange={handleInputChange}
                className="border-gray-300 focus:border-teal-700 focus:ring-teal-700"
                placeholder="e.g., 500-1000 prescriptions per month"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-navy-dark font-medium">
                What capacity do you need? *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={5}
                className="border-gray-300 focus:border-teal-700 focus:ring-teal-700"
                placeholder="Please describe your current capacity challenges and requirements..."
              />
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full bg-teal-700 hover:bg-teal-800 text-white text-lg"
            >
              {isSubmitting ? 'Submitting...' : 'Request Discussion'}
            </Button>

            <p className="text-sm text-gray-600 text-center pt-2">
              By submitting, you agree to our <a href="/privacy-policy" className="text-teal-700 hover:text-teal-800 underline">Privacy Policy</a>. We will use your details to respond to your enquiry.
            </p>
            
            <p className="text-sm text-gray-600 text-center">
              We respond to professional enquiries within one business day.
            </p>

            <div className="text-center mt-6 pt-6 border-t border-gray-300">
              <p className="text-xs text-gray-500 mb-1">Weight Prescriber Network</p>
              <p className="text-xs text-gray-500">261 High Street, Waltham Cross, EN8 7BE</p>
              <p className="text-xs text-gray-500">Company Number: 14702569 | ICO Registration: 0018850446</p>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-dark text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Weight Prescriber Network</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Pharmacist-led prescribing infrastructure delivering scalable, per-task capacity to 
                regulated UK weight management providers.
              </p>
              <div className="text-sm text-gray-400 space-y-1">
                <p>261 High Street, Waltham Cross, EN8 7BE</p>
                <p>Company Number: 14702569</p>
                <p>ICO Registration: 0018850446</p>
              </div>
            </div>
            <div className="flex flex-col md:items-end">
              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 mb-1">Phone</p>
                  <a
                    href="tel:01992329953"
                    className="text-teal-400 hover:text-teal-300 transition-colors"
                  >
                    01992 329953
                  </a>
                </div>
                <div>
                  <p className="text-gray-400 mb-1">Email</p>
                  <a
                    href="mailto:info@weightprescribernetwork.co.uk"
                    className="text-teal-400 hover:text-teal-300 transition-colors"
                  >
                    info@weightprescribernetwork.co.uk
                  </a>
                </div>
                <div>
                  <p className="text-gray-400 mb-1">LinkedIn</p>
                  <a
                    href="https://www.linkedin.com/company/weight-prescriber-network/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors"
                  >
                    <Linkedin size={20} />
                    <span>Follow us on LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex gap-6 text-sm flex-wrap justify-center">
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  About
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Contact
                </button>
                <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </a>
                <a href="/terms-conditions" className="text-gray-400 hover:text-white transition-colors">
                  Terms & Conditions
                </a>
              </div>
              <p className="text-sm text-gray-400">
                © 2025 Weight Prescriber Network, UK. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
};
